/**
 * Retrouve dans la page l'élément qu'une scène de visite guidée désigne.
 *
 * Une cible se décrit comme un lecteur d'écran la perçoit — un rôle et un nom
 * accessible — plutôt que par une classe CSS : les vues n'ont rien à exposer
 * pour être guidées, et la visite suit la langue active puisque le nom attendu
 * est traduit avec les mêmes clés que la vue.
 *
 * Formes acceptées (combinables par `anyOf`) :
 *   { selector: "[data-ak-v2-component='page-header']" }
 *   { role: "button", name: "Nouveau secteur" }            nom exact (casse/espaces ignorés)
 *   { role: "textbox", name: "Nom", match: "includes" }    nom partiel
 *   { role: "button", name: "Créer", within: { role: "dialog" } }
 *   { text: "Secteur rattaché", closest: ".form-group" }  libellé visible d'un champ sans nom
 *   { text: "Secteur lié", closest: ".row", then: { role: "combobox" } }  son contrôle
 *   { anyOf: [descriptorA, descriptorB] }
 */

const ROLE_SELECTORS = Object.freeze({
  button: "button, [role='button'], input[type='button'], input[type='submit']",
  link: "a[href], [role='link']",
  textbox: "input:not([type]), input[type='text'], input[type='email'], input[type='search'], input[type='password'], input[type='number'], input[type='tel'], input[type='url'], textarea, [role='textbox'], [contenteditable='true']",
  combobox: "select, [role='combobox']",
  checkbox: "input[type='checkbox'], [role='checkbox'], [role='switch']",
  radio: "input[type='radio'], [role='radio']",
  tab: "[role='tab']",
  dialog: "[role='dialog'], [role='alertdialog']",
  heading: "h1, h2, h3, h4, h5, h6, [role='heading']",
  navigation: "nav, [role='navigation']",
  menuitem: "[role='menuitem'], [role='menuitemradio'], [role='menuitemcheckbox']",
  region: "section[aria-label], section[aria-labelledby], [role='region']",
  main: "main, [role='main']",
  searchbox: "input[type='search'], [role='searchbox']",
});

export const TOUR_TARGET_ROLES = Object.freeze(Object.keys(ROLE_SELECTORS));

const collapse = (value) => String(value ?? "").replace(/\s+/g, " ").trim().toLocaleLowerCase();

const textOfIds = (element, attribute) => {
  const ids = (element.getAttribute(attribute) || "").split(/\s+/).filter(Boolean);
  const documentRef = element.ownerDocument;
  return ids.map((id) => documentRef.getElementById(id)?.textContent || "").join(" ");
};

/**
 * Texte perçu d'un sous-arbre : les pictos décoratifs (`aria-hidden`) sont
 * ignorés et chaque élément est séparé de son voisin, comme le lit un
 * lecteur d'écran (« Comptes 7 » et non « Comptes7 »).
 */
export function perceivedText(node) {
  if (!node) return "";
  if (node.nodeType === 3) return node.nodeValue || "";
  if (node.nodeType !== 1 || node.getAttribute("aria-hidden") === "true") return "";
  const tag = node.tagName.toLowerCase();
  if (["script", "style", "svg", "template"].includes(tag)) return "";
  return Array.from(node.childNodes).map(perceivedText).join(" ");
}

/** Nom accessible simplifié : suffisant pour les contrôles des vues V2. */
export function accessibleName(element) {
  if (!element || element.nodeType !== 1) return "";
  const labelledBy = textOfIds(element, "aria-labelledby");
  if (collapse(labelledBy)) return collapse(labelledBy);
  const ariaLabel = element.getAttribute("aria-label");
  if (collapse(ariaLabel)) return collapse(ariaLabel);
  const tag = element.tagName.toLowerCase();
  if (["input", "textarea", "select"].includes(tag)) {
    const labels = element.labels ? Array.from(element.labels).map(perceivedText).join(" ") : "";
    if (collapse(labels)) return collapse(labels);
    const wrapping = element.closest("label");
    if (wrapping && collapse(perceivedText(wrapping))) return collapse(perceivedText(wrapping));
    if (tag === "input" && ["button", "submit"].includes(element.type)) return collapse(element.value);
    return collapse(element.getAttribute("placeholder") || element.getAttribute("title"));
  }
  const text = collapse(perceivedText(element));
  return text || collapse(element.getAttribute("title"));
}

/** Visible et non inerte : une cible cachée ne peut ni être montrée ni cliquée. */
// Racine de l'application : une fenêtre V1 (reactstrap) y pose `aria-hidden`
// et l'oublie parfois en se fermant. Sans fenêtre ouverte, cet attribut oublié
// ne doit pas rendre la page entière invisible à la visite guidée.
// La bulle de la visite et la carte d'avis sont elles-mêmes des `dialog` : elles
// ne comptent pas comme une fenêtre de la page (sans cette exception, la règle
// ne jouait jamais pendant une leçon, et la page restait voilée tout entière).
const TOUR_OWN_LAYERS = "[data-ak-v2-component='guided-tour-card'], [data-ak-v2-component='lesson-feedback']";
const pageDialogOpen = (document) => Array.from(document.querySelectorAll("[role='dialog'], [role='alertdialog']"))
  .some((dialog) => !dialog.closest(TOUR_OWN_LAYERS));
const staleAppHiding = (hidden, element) => hidden.id === "app" && !pageDialogOpen(element.ownerDocument);

export function isTourTargetVisible(element) {
  if (!element || !element.isConnected) return false;
  const hidden = element.closest("[inert], [aria-hidden='true']");
  if (hidden && !staleAppHiding(hidden, element)) return false;
  const view = element.ownerDocument.defaultView;
  const style = view?.getComputedStyle?.(element);
  if (style && (style.visibility === "hidden" || style.display === "none")) return false;
  const rect = element.getBoundingClientRect();
  // jsdom ne calcule aucune géométrie : on ne peut pas y exclure sur la taille.
  const measurable = rect.width > 0 || rect.height > 0 || view?.navigator?.userAgent?.includes("jsdom");
  return Boolean(measurable);
}

/**
 * Qualité de la correspondance d'un nom : 3 exacte, 2 suivie d'un compteur ou
 * d'une description (« Comptes 7 »), 1 précédée d'un picto non masqué
 * (« ＋ Nouveau secteur ») ou incluse sur demande, 0 aucune.
 */
const nameScore = (element, expected, match) => {
  if (expected === undefined || expected === null) return 3;
  const actual = accessibleName(element);
  if (expected instanceof RegExp) return expected.test(actual) ? 3 : 0;
  const wanted = (Array.isArray(expected) ? expected.map(collapse) : [collapse(expected)]).filter(Boolean);
  return Math.max(0, ...wanted.map((name) => {
    if (actual === name) return 3;
    if (match === "includes") return actual.includes(name) ? 1 : 0;
    if (actual.startsWith(`${name} `)) return 2;
    return actual.endsWith(` ${name}`) ? 1 : 0;
  }));
};

/**
 * Champ dont le nom accessible manque (formulaire V1 embarqué) : on le trouve
 * par le libellé VISIBLE qui le précède, puis on éclaire son conteneur.
 */
const byVisibleText = (descriptor, root) => {
  const names = (Array.isArray(descriptor.text) ? descriptor.text : [descriptor.text]).map(collapse).filter(Boolean);
  if (!names.length) return [];
  const walker = (root.ownerDocument || root).createTreeWalker(root, 1 /* NodeFilter.SHOW_ELEMENT */);
  const found = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const own = collapse(Array.from(node.childNodes).filter((child) => child.nodeType === 3).map((child) => child.nodeValue).join(" "));
    if (!own) continue;
    const trimmed = own.replace(/\s*\*$/, "");
    if (names.some((name) => trimmed === name)) {
      found.push(descriptor.closest ? node.closest(descriptor.closest) || node : node);
    }
  }
  return [...new Set(found)];
};

const candidatesFor = (descriptor, root) => {
  if (descriptor.text) return byVisibleText(descriptor, root);
  if (descriptor.selector) return Array.from(root.querySelectorAll(descriptor.selector));
  const selector = ROLE_SELECTORS[descriptor.role];
  if (!selector) return [];
  return Array.from(root.querySelectorAll(selector));
};

/**
 * Tous les éléments visibles correspondant au descripteur, dans l'ordre du
 * document. La portée `within` retient le DERNIER conteneur visible : c'est la
 * boîte de dialogue ouverte le plus récemment, au-dessus des autres.
 */
export function findTourTargets(descriptor, root = typeof document !== "undefined" ? document : null) {
  if (!descriptor || !root) return [];
  if (Array.isArray(descriptor.anyOf)) {
    for (const option of descriptor.anyOf) {
      const found = findTourTargets(option, root);
      if (found.length) return found;
    }
    return [];
  }
  let scope = root;
  if (descriptor.within) {
    const containers = findTourTargets(descriptor.within, root);
    // Le conteneur le plus récent dans le document : la fenêtre ouverte en dernier.
    scope = containers.reduce((latest, candidate) => (
      !latest || latest.compareDocumentPosition(candidate) & 4 /* FOLLOWING */ ? candidate : latest
    ), null);
    if (!scope) return [];
  }
  // Les meilleures correspondances d'abord, dans l'ordre du document à égalité.
  const found = candidatesFor(descriptor, scope)
    .map((element, index) => ({ element, index, score: descriptor.text ? 3 : nameScore(element, descriptor.name, descriptor.match) }))
    .filter(({ element, score }) => score > 0 && isTourTargetVisible(element))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ element }) => element);
  // `then` : le contrôle que ce conteneur porte (le champ d'un libellé visible).
  if (!descriptor.then) return found;
  return found.flatMap((container) => findTourTargets(descriptor.then, container));
}

/**
 * Élément à éclairer pour un candidat trouvé. Un champ de choix react-select
 * pose `role="combobox"` sur un input d'un pixel de large : on éclaire son
 * contrôle entier (valeur, flèche), que la personne peut cliquer partout —
 * sinon la découpe n'est qu'un carré au bord gauche du champ.
 */
const CHOICE_CONTROL = "[class*='__control'], [class*='-control']";
export function tourTargetSurface(element) {
  if (!element || element.tagName !== "INPUT" || element.getAttribute("role") !== "combobox") return element;
  const control = element.closest(CHOICE_CONTROL);
  return control && control !== element && !control.classList.contains("form-control") ? control : element;
}

export function resolveTourTarget(descriptor, root) {
  const found = findTourTargets(descriptor, root);
  return tourTargetSurface(found[0]) || null;
}
